import { useState, useContext, useEffect, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";
import { SiteContext } from "@/App";
import { Loader2, Upload, Image } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

export default function SettingsTab({ token, onSettingsUpdate }) {
  const { API, settings } = useContext(SiteContext);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    hero_image_url: settings?.hero_image_url || "",
    hero_event_enabled: settings?.hero_event_enabled || false,
    hero_event_title: settings?.hero_event_title || "",
    hero_event_description: settings?.hero_event_description || "",
    hero_event_link: settings?.hero_event_link || "",
    hero_event_date: settings?.hero_event_date || "",
    instagram_handle: settings?.instagram_handle || "ludbradleytattoo",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (settings) setForm({
      hero_image_url: settings.hero_image_url || "",
      hero_event_enabled: settings.hero_event_enabled || false,
      hero_event_title: settings.hero_event_title || "",
      hero_event_description: settings.hero_event_description || "",
      hero_event_link: settings.hero_event_link || "",
      hero_event_date: settings.hero_event_date || "",
      instagram_handle: settings.instagram_handle || "ludbradleytattoo",
    });
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onSettingsUpdate(data);
      toast.success("Settings saved.");
    } catch {
      toast.error("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB.");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await axios.post(`${API}/settings/hero-upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" }
      });
      onSettingsUpdate(data);
      setForm(prev => ({ ...prev, hero_image_url: data.hero_image_url }));
      toast.success("Hero image uploaded!");
    } catch {
      toast.error("Upload failed.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-5 py-4">
      {/* Hero Image */}
      <div>
        <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">Hero Image</label>
        <div className="flex gap-2">
          <input
            data-testid="settings-hero-image"
            value={form.hero_image_url}
            onChange={e => setForm(p => ({ ...p, hero_image_url: e.target.value }))}
            className="flex-1 bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none transition-colors"
            placeholder="https://... or upload below"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden"
          />
          <button
            data-testid="settings-hero-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-2 bg-ink-800 border border-ink-600 hover:border-verde text-[#A3A3A3] hover:text-verde font-manrope text-[10px] tracking-widest uppercase px-4 py-2 rounded-sm transition-colors disabled:opacity-60"
          >
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            {uploading ? "Uploading..." : "Upload"}
          </button>
        </div>
        {form.hero_image_url && (
          <div className="mt-3 rounded-sm overflow-hidden border border-ink-600 max-w-xs">
            <img
              src={form.hero_image_url}
              alt="Hero preview"
              className="w-full h-32 object-cover"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </div>
        )}
      </div>

      <div>
        <label className="font-manrope text-[10px] tracking-widest uppercase text-[#525252] block mb-2">Instagram Handle</label>
        <input
          data-testid="settings-instagram-handle"
          value={form.instagram_handle}
          onChange={e => setForm(p => ({ ...p, instagram_handle: e.target.value }))}
          className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none transition-colors"
        />
      </div>

      <div className="border-t border-ink-600 pt-4">
        <div className="flex items-center justify-between mb-4">
          <Label className="font-manrope text-[10px] tracking-widest uppercase text-[#A3A3A3]">Enable Event Overlay</Label>
          <Switch
            data-testid="settings-event-toggle"
            checked={form.hero_event_enabled}
            onCheckedChange={v => setForm(p => ({ ...p, hero_event_enabled: v }))}
          />
        </div>
        {form.hero_event_enabled && (
          <div className="space-y-3">
            <input
              data-testid="settings-event-title"
              value={form.hero_event_title}
              onChange={e => setForm(p => ({ ...p, hero_event_title: e.target.value }))}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none"
              placeholder="Event Title"
            />
            <input
              data-testid="settings-event-date"
              value={form.hero_event_date}
              onChange={e => setForm(p => ({ ...p, hero_event_date: e.target.value }))}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none"
              placeholder="Date (e.g. March 15, 2025)"
            />
            <textarea
              data-testid="settings-event-description"
              value={form.hero_event_description}
              onChange={e => setForm(p => ({ ...p, hero_event_description: e.target.value }))}
              rows={2}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none resize-none"
              placeholder="Event description..."
            />
            <input
              data-testid="settings-event-link"
              value={form.hero_event_link}
              onChange={e => setForm(p => ({ ...p, hero_event_link: e.target.value }))}
              className="w-full bg-ink-800 border border-ink-600 focus:border-verde text-[#E5E5E5] font-manrope text-xs px-3 py-2 rounded-sm outline-none"
              placeholder="Event URL (https://...)"
            />
          </div>
        )}
      </div>

      <button data-testid="settings-save-btn" onClick={handleSave} disabled={saving} className="w-full btn-verde py-3 mt-2 disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin mx-auto" /> : "Save Settings"}
      </button>
    </div>
  );
}
